import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Camera, Search, ChevronRight } from "lucide-react";
import { api, gbp, fmtDate } from "@/lib/api";
import { cleanName } from "@/components/site/EstimateCard";
import { STATUS_LABELS, STATUS_STYLES } from "@/lib/options";

export const StatusPill = ({ s }) => <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[s] || ""}`} data-testid="status-pill">{STATUS_LABELS[s] || s}</span>;

export const estText = (e) => {
  const x = e.valuation?.estimate;
  if (!x) return null;
  return x.low ? `${gbp(x.low)} – ${gbp(x.high)}` : gbp(x.amount);
};
export const isManual = (e) => e.valuation?.status === "manual_review" || !estText(e);
export const apiValue = (e) => (e.valuation?.dealer_estimate ? gbp(e.valuation.dealer_estimate.amount) : null);
export const yearOf = (e) => {
  const d = e.vehicle_details || [];
  return d.find((x) => x.label === "Year")?.value || d.find((x) => x.label === "First registered")?.value?.slice(0, 4);
};

const Row = ({ e }) => {
  const est = estText(e);
  return (
    <Link to={`/dealer/enquiries/${e.id}`} className="group flex flex-col gap-3 rounded-lg border border-border bg-white p-4 transition-colors duration-150 hover:border-brand-navy sm:flex-row sm:items-center sm:gap-6" data-testid={`enquiry-row-${e.reference}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {!e.seen_at && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-brand-red" title="Not opened yet" data-testid="unseen-indicator" />}
          <p className="truncate text-base font-bold text-slate-900 sm:text-lg" data-testid="enquiry-row-title">{yearOf(e) ? `${yearOf(e)} ` : ""}{cleanName(e.vehicle_description) || "Vehicle details unavailable"}</p>
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-700">
          <span className="plate-sm">{e.registration}</span>
          <span>{Number(e.mileage).toLocaleString("en-GB")} miles</span>
          {e.photo_count > 0 && <span className="inline-flex items-center gap-0.5 text-xs text-slate-600"><Camera className="h-3.5 w-3.5" />{e.photo_count}</span>}
          {e.is_test && <span className="rounded border border-amber-300 bg-amber-50 px-1.5 text-[11px] font-semibold text-amber-900" data-testid="test-row-badge">Test</span>}
          {e.valuation?.status === "manual_review" && <span className="rounded border border-slate-300 bg-slate-50 px-1.5 text-[11px] font-semibold text-slate-700" data-testid="manual-review-badge">Manual Review Required</span>}
        </p>
        <p className="mt-1 text-sm text-slate-600"><span className="font-semibold text-slate-800" data-testid="enquiry-row-name">{e.contact?.name}</span> · <span data-testid="enquiry-row-phone">{e.contact?.phone}</span> · <span data-testid="enquiry-row-postcode">{e.contact?.postcode || "Postcode not given"}</span> · {fmtDate(e.created_at)}</p>
      </div>
      <div className="sm:w-48 sm:text-right">
        <p className="text-xs text-slate-500">Estimated value</p>
        <p className={!isManual(e) ? "text-lg font-bold text-slate-900" : "text-sm font-medium text-slate-600"} data-testid="enquiry-row-value">{!isManual(e) ? est : "Manual Review Required"}</p>
        {isManual(e) && apiValue(e) && <p className="text-xs text-slate-500" data-testid="enquiry-row-api-value">Internal estimate {apiValue(e)} (not shown to customer)</p>}
      </div>
      <ChevronRight className="hidden h-5 w-5 text-slate-400 transition-transform duration-150 group-hover:translate-x-0.5 sm:block" />
    </Link>
  );
};

const VIEWS = [["new", "New"], ["interested", "Interested"], ["not_interested", "Not Interested"]];
const EMPTY = { new: "No new enquiries right now.", interested: "No vehicles marked Interested yet.", not_interested: "No vehicles marked Not Interested." };

export default function Enquiries({ test }) {
  const ctx = useOutletContext();
  const [view, setView] = useState("new");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const s = ctx?.summary;

  useEffect(() => { setView("new"); setPage(1); }, [test]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/dealer/enquiries", { params: { ...(test ? { test } : {}), page, view, ...(q ? { q } : {}) } })
        .then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, pages: 1 }));
    }, 200);
    return () => clearTimeout(t);
  }, [view, q, test, page]);

  return (
    <div data-testid={test ? "test-enquiries-page" : "enquiries-page"}>
      <h1 className="text-2xl font-extrabold text-slate-900 sm:text-3xl" data-testid="enquiries-heading">{test ? "Test & demo enquiries" : "Vehicle Enquiries"}</h1>
      <p className="mt-1 text-sm text-slate-600">{test ? "Sandbox and demo enquiries, kept separate from real customers." : "Cars offered to the dealership – newest first. Click a car to see the details."}</p>
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="inline-flex rounded-lg border border-border bg-white p-1" role="tablist" aria-label="Show">
          {VIEWS.map(([k, label]) => (
            <button key={k} role="tab" aria-selected={view === k} onClick={() => { setView(k); setPage(1); }}
                    className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors duration-150 sm:px-4 ${view === k ? "bg-brand-navy text-white" : "text-slate-700 hover:bg-slate-100"}`} data-testid={`view-filter-${k}`}>
              {label}{k === "new" && !test && s?.new ? ` (${s.new})` : ""}
            </button>
          ))}
        </div>
        <div className="relative flex-1 sm:ml-auto sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input className="field-input py-2 pl-9 text-sm" placeholder="Search reg, name or postcode" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search enquiries" data-testid="enquiry-search-input" />
        </div>
      </div>
      <div className="mt-4 space-y-2.5" data-testid="enquiry-list">
        {data === null ? <p className="rounded-lg bg-white p-6 text-slate-500">Loading…</p>
          : data.items.length === 0 ? <p className="rounded-lg border border-dashed border-border bg-white p-8 text-center text-slate-600" data-testid="enquiries-empty">{EMPTY[view]}</p>
            : data.items.map((e) => <Row key={e.id} e={e} />)}
      </div>
      {data && data.pages > 1 && (
        <div className="mt-4 flex items-center gap-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border bg-white px-3 py-1.5 disabled:opacity-40" data-testid="page-prev">Previous</button>
          <span>Page {page} of {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded border bg-white px-3 py-1.5 disabled:opacity-40" data-testid="page-next">Next</button>
        </div>
      )}
      {test && <p className="mt-8 text-sm text-slate-500"><Link to="/dealer" className="link" data-testid="back-to-real-link">Back to all enquiries</Link></p>}
    </div>
  );
}
